import React from "react";
import BackgroundCover from "../../components/backgroundCover/BackgroundCover";
import TitleWithDescription from "../../components/TitleWithDescription/TitleWithDescription";
import TimelineSection from "../../components/TimelineSection/TimelineSection";
import Title from "../../components/Title/Title";
import { useLanguage } from "../../context/LanguageContext";
import { getMoneyArtTranslations } from "../../translations/moneyArtTemplate";
import { isMobileOrTablet } from "../../utils/appUtils";
import { isMobile } from "../../utils/appUtils";
import { useEditMode } from "../../context/EditModeContext";
import EditableText from "../../components/EditableText/EditableText";
import { usePageContent } from "../../hooks/usePageContent";
import { MoneyArtTranslations } from "../../translations/moneyArtTemplate";

function MoneyArt() {
    const { language } = useLanguage();
    const { isEditMode } = useEditMode();
    const { content, loading } = usePageContent<MoneyArtTranslations>('money_art');

    // Use translations as fallback content
    const t = content || getMoneyArtTranslations(language);

    // Show loading state if needed
    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin h-12 w-12 border-2 border-gray-400 rounded-full border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="">
            <BackgroundCover
                backgroundHeight={isMobileOrTablet() ? (isMobile() ? 400 : 600) : undefined}
                imagePosition={isMobileOrTablet() ? (isMobile() ? "center" : "initial") : "initial"} 
                backgroundImage="/images/money_art/money_art_bg.jpg"
            >
                <div className="absolute top-1/2 left-[5%] -translate-y-1/2 z-10 hidden md:block">
                    <Title
                        firstLine={isEditMode ? (
                            <EditableText
                                contentKey="money_art.title.firstLine"
                                defaultValue={t.title.firstLine}
                            />
                        ) : t.title.firstLine}
                        secondLine={isEditMode ? (
                            <EditableText
                                contentKey="money_art.title.secondLine"
                                defaultValue={t.title.secondLine}
                            />
                        ) : t.title.secondLine}
                        className="hidden md:block"
                        animate={true}
                    />
                </div>
            </BackgroundCover>

            <div className="mx-auto px-4 py-6 md:py-24">
                <div className="block md:hidden mb-8">
                    <Title
                        firstLine={isEditMode ? (
                            <EditableText
                                contentKey="money_art.title.firstLine"
                                defaultValue={t.title.firstLine}
                            />
                        ) : t.title.firstLine}
                        secondLine={isEditMode ? (
                            <EditableText
                                contentKey="money_art.title.secondLine"
                                defaultValue={t.title.secondLine}
                            />
                        ) : t.title.secondLine}
                        className=""
                        animate={true}
                    />
                </div>
                <div className="flex flex-col items-center">
                    <div className="max-w-4xl text-sm text-gray-600 leading-relaxed">
                        {isEditMode ? (
                            <EditableText
                                contentKey="money_art.introduction"
                                defaultValue={t.introduction}
                                multiline={true}
                            />
                        ) : t.introduction}
                    </div>
                </div>
            </div>

            <div className="relative w-full overflow-hidden">
                <div className="w-full bg-gradient-to-b from-[#E1D8D3] via-[#FFFFFF] to-[#E1D8D3] pb-12 md:py-12">
                    <div className="mx-auto px-4 md:px-12 lg:pt-8 max-w-6xl">
                        {t.sections.map((section, index) => (
                            <div key={index} className="mb-16 md:mb-24">
                                <div className="flex flex-col md:flex-row items-center">
                                    {/* Text section - always on left */}
                                    <div className="w-full md:w-2/3 md:pr-8 mb-8 md:mb-0 flex flex-col justify-center">
                                        <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                                            {isEditMode ? (
                                                <EditableText
                                                    contentKey={`money_art.sections.${index}.description`}
                                                    defaultValue={section.description}
                                                    multiline={true}
                                                />
                                            ) : section.description}
                                        </div>
                                    </div>
                                    
                                    {/* Image section - always on right */}
                                    <div className="w-full md:w-1/3">
                                        {section.imageTitle && (
                                            <h3 className="text-xl md:text-2xl font-semibold mb-4">
                                                {isEditMode ? (
                                                    <EditableText
                                                        contentKey={`money_art.sections.${index}.imageTitle`}
                                                        defaultValue={section.imageTitle}
                                                    />
                                                ) : section.imageTitle}
                                            </h3>
                                        )}
                                        <div className="mb-4">
                                            <img 
                                                src={`/images/money_art/Money_Art_${index + 1}.jpg`}
                                                alt={`Money Art Page ${index + 1}`}
                                                className="h-auto max-h-[500px] object-contain ml-0"
                                            />
                                        </div>
                                        {section.imageCaption && (
                                            <div className="text-sm text-gray-600 mt-2 italic whitespace-pre-line">
                                                {isEditMode ? (
                                                    <EditableText
                                                        contentKey={`money_art.sections.${index}.imageCaption`}
                                                        defaultValue={section.imageCaption}
                                                        multiline={true}
                                                    />
                                                ) : section.imageCaption}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Line separator between sections */}
                                {index !== t.sections.length - 1 && (
                                    <div className="w-full border-b border-gray-300 my-16"></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MoneyArt; 